import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useTranslation } from "../context/LanguageContext";
import { FaSyringe, FaCheck, FaTimes, FaTrash, FaPlus } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import "../styles/common.css";
import "./ModifierSante.css";

export default function ModifierSante() {
  const { t } = useTranslation();
  const { id } = useParams(); // ✅ récupère l’ID depuis l’URL
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    animal: "",
    type_intervention: "",
    statut: "",
    date: "",
    description: "",
    prochaine_intervention: "",
  });

    const [animaux, setAnimaux] = useState([]);
    const [stocksDisponibles, setStocksDisponibles] = useState([]);
    const [produitsUtilises, setProduitsUtilises] = useState([]);
    const [initialProduitsUtilises, setInitialProduitsUtilises] = useState([]);
    const [initialData, setInitialData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
      animal: "",
      type_intervention: "",
      date: "",
    });
    const [touchedFields, setTouchedFields] = useState({
      animal: false,
      type_intervention: false,
      date: false,
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);

     // ✅ Charger les infos de l'animal spécifique
  useEffect(() => {
    // Note: Utilisation d'un chemin d'API correct
    const fetchSante = async () => {
      try {
        const response = await api.get(`/api/sante/${id}/`);
        const data = response.data;
        setFormData({
          animal: data.animal || "",
          type_intervention: data.type_intervention || "",
          statut: data.statut || "",
          date: data.date || "",
          description: data.description || "",
          prochaine_intervention: data.prochaine_intervention || "",
        });
        setInitialData(data);
        
        // Charger les produits utilisés depuis le champ 'utilisations' de l'API
        if (data.utilisations && Array.isArray(data.utilisations)) {
          const produits = data.utilisations.map(u => ({
            stock_id: u.stock || u.stock_id || "",
            quantite_utilisee: u.quantite_utilisee || "",
            notes: u.notes || "",
          }));
          setProduitsUtilises(produits);
          setInitialProduitsUtilises(produits);
        }
        setLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement de la fiche de santé:", error);
        setLoading(false);
      }
    };
    fetchSante();
  }, [id]);

  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [animauxRes, stocksRes] = await Promise.all([
          api.get("/api/animaux/"),
          api.get("/api/stocks-pharmacie/"), // Charger TOUS les stocks, pas seulement les disponibles
        ]);
        setAnimaux(animauxRes.data);
        setStocksDisponibles(stocksRes.data);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      }
    };
    fetchData();
  }, []); // Exécute une seule fois au montage du composant
  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  const validateField = (name, value) => {
    if (name === "animal" || name === "type_intervention" || name === "date") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  // ✅ Gérer la saisie
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Valider le champ
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const hasChanges = () => {
    if (!initialData) return false;
    const normalize = (val) => (val === null || val === undefined) ? "" : String(val).trim();
    
    // Comparer les données du formulaire
    const formDataChanged = (
      String(formData.animal) !== String(initialData.animal || "") ||
      normalize(formData.type_intervention) !== normalize(initialData.type_intervention) ||
      normalize(formData.statut) !== normalize(initialData.statut) ||
      normalize(formData.date) !== normalize(initialData.date) ||
      normalize(formData.description) !== normalize(initialData.description) ||
      normalize(formData.prochaine_intervention) !== normalize(initialData.prochaine_intervention)
    );
    
    // Comparer les produits utilisés
    const produitsChanged = JSON.stringify(produitsUtilises) !== JSON.stringify(initialProduitsUtilises);
    
    return formDataChanged || produitsChanged;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      animal: true,
      type_intervention: true,
      date: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      animal: validateField("animal", formData.animal),
      type_intervention: validateField("type_intervention", formData.type_intervention),
      date: validateField("date", formData.date),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      const dataToSend = {
        ...formData,
        produits_utilises: produitsUtilises.map(p => ({
          stock_id: p.stock_id,
          quantite_utilisee: parseFloat(p.quantite_utilisee || 0),
          notes: p.notes || "",
        })),
      };
      await api.put(`/api/sante/${id}/`, dataToSend);
      toast.success("Fiche de santé modifiée avec succès !");
      setTimeout(() => {
        navigate("/sante");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la mise à jour.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddProduit = () => {
    setProduitsUtilises([...produitsUtilises, {
      stock_id: "",
      quantite_utilisee: "",
      notes: "",
    }]);
  };

  const handleRemoveProduit = (index) => {
    setProduitsUtilises(produitsUtilises.filter((_, i) => i !== index));
  };

  const handleProduitChange = (index, field, value) => {
    const updated = [...produitsUtilises];
    updated[index] = { ...updated[index], [field]: value };
    setProduitsUtilises(updated);
  };

  const getProduitUtiliseLabel = (stockId) => {
    const stock = stocksDisponibles.find(s => s.id === parseInt(stockId));
    if (!stock) return "Non sélectionné";
    return `${stock.produit_nom} (${stock.quantite || stock.quantite_disponible || stock.stock_disponible || 0} ${stock.produit_unite || ""} disponible)`;
  };

  const handleDeleteConfirm = async () => {
    setIsSubmitting(true);
    try {
      await api.delete(`/api/sante/${id}/`);
      setShowDeleteModal(false);
      toast.success("Fiche de santé supprimée avec succès !");
      setTimeout(() => {
        navigate("/sante");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la suppression.";
      toast.error(errorMessage);
      setShowDeleteModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (loading) {
    return (
      <div className="modifier-sante-page">
        <div className="loading-container">
          <p className="loading-text">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modifier-sante-page">
      <div className="title-badge">
        <FaSyringe className="page-title-icon" aria-hidden="true" />
        <span>{t.editHealthCard}</span>
      </div>
      <form onSubmit={handleSubmit} className="modifier-sante-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="animal">
              {t.animal} <span className="required-indicator">*</span>
            </label>
            <select
              id="animal"
              name="animal"
              value={formData.animal || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.animal && touchedFields.animal ? "input-error" : ""}
            >
              <option value="">{t.selectAnimal}</option>
              {animaux.map((animal) => (
                <option key={animal.id} value={animal.id}>
                  {animal.matricule}
                </option>
              ))}
            </select>
            {fieldErrors.animal && touchedFields.animal && (
              <span className="field-error-message">{fieldErrors.animal}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="type_intervention">
              Type d'intervention <span className="required-indicator">*</span>
            </label>
            <select
              id="type_intervention"
              name="type_intervention"
              value={formData.type_intervention || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.type_intervention && touchedFields.type_intervention ? "input-error" : ""}
            >
              <option value="">{t.selectType}</option>
              <option value="vaccination">{t.vaccination}</option>
              <option value="traitement">{t.treatment}</option>
              <option value="vermifuge">{t.deworming}</option>
              <option value="autre">{t.other}</option>
            </select>
            {fieldErrors.type_intervention && touchedFields.type_intervention && (
              <span className="field-error-message">{fieldErrors.type_intervention}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="statut">Statut</label>
            <select
              id="statut"
              name="statut"
              value={formData.statut || ""}
              onChange={handleChange}
            >
              <option value="traite">{t.treated}</option>
              <option value="non_traite">{t.notTreated}</option>
            </select>
          </div>

          <div className="form">
            <label htmlFor="date">
              Date <span className="required-indicator">*</span>
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.date && touchedFields.date ? "input-error" : ""}
            />
            {fieldErrors.date && touchedFields.date && (
              <span className="field-error-message">{fieldErrors.date}</span>
            )}
          </div>

          {/* Section Produits utilisés */}
          <div className="form full-width">
            <label>
              Produits utilisés
            </label>
            <div className="produits-utilises-container">
              {produitsUtilises.map((produit, index) => (
                <div key={index} className="produit-utilise-row">
                  <select
                    value={produit.stock_id || ""}
                    onChange={(e) => handleProduitChange(index, "stock_id", e.target.value)}
                    className="produit-select"
                  >
                    <option value="">{t.selectStock}</option>
                    {stocksDisponibles.map((stock) => (
                      <option key={stock.id} value={stock.id}>
                        {stock.produit_nom} - {stock.quantite || stock.quantite_disponible || stock.stock_disponible || 0} {stock.produit_unite || ""}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder={t.quantityLabel}
                    value={produit.quantite_utilisee || ""}
                    onChange={(e) => handleProduitChange(index, "quantite_utilisee", e.target.value)}
                    className="quantite-input"
                  />
                  <input
                    type="text"
                    placeholder={t.notesOptional}
                    value={produit.notes || ""}
                    onChange={(e) => handleProduitChange(index, "notes", e.target.value)}
                    className="notes-input"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveProduit(index)}
                    className="remove-produit-button"
                    aria-label="Supprimer ce produit"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddProduit}
                className="add-produit-button"
              >
                <FaPlus />
                <span>Ajouter un produit</span>
              </button>
            </div>
          </div>

          <div className="form full-width">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description || ""}
              onChange={handleChange}
              placeholder={t.interventionDescription}
              rows="3"
            ></textarea>
          </div>

          <div className="form">
            <label htmlFor="prochaine_intervention">Prochaine intervention</label>
            <input
              type="date"
              id="prochaine_intervention"
              name="prochaine_intervention"
              value={formData.prochaine_intervention || ""}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!hasChanges() || loading || isSubmitting}
          >
            <FaCheck />
            <span>{isSubmitting ? t.saving : t.save}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="delete-but"
            disabled={loading || isSubmitting}
          >
            <FaTrash />
            <span>Supprimer</span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/sante")}
            className="cancel-but"
            disabled={loading || isSubmitting}
          >
            <FaTimes />
            <span>{t.cancel}</span>
          </button>
        </div>
      </form>

      {/* Modal de suppression */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        itemName=""
        itemType="fiche de santé"
        consequences={[]}
        requireConfirmation={false}
        isLoading={isSubmitting}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
